import type { CSSProperties, FC, MouseEventHandler, MouseEvent } from 'react';
import { useMemo, useCallback } from 'react';
import { Button, Skeleton } from 'antd-mobile';
import classNames from 'classnames';
import { useResource } from '@axios-use/react';
import { getCommonConfigRefConfig, type Config as CommonConfigType } from '@/apis/home';
import { getLocalPrivacyVersionValue } from './config';

import styles from './styles/confirm.module.less';

export type PrivacyContentProps = {
  className?: string;
  style?: CSSProperties;
  onLink?: MouseEventHandler<HTMLSpanElement>;
};

const PrivacyContent: FC<PrivacyContentProps> = (props) => {
  const { className, style, onLink } = props;

  return (
    <div className={classNames(styles.privacyContent, className)} style={style}>
      <div className={styles.privacyContentTitle}>麦当劳隐私政策提示</div>
      <div className={styles.privacyConfirmScroll}>
        <div className={styles.privacyContentText}>感谢您对麦当劳一直以来的信任！</div>
        <div className={styles.privacyContentText}>
          为更好地保护您的个人信息安全，请您务必仔细阅读并充分理解我们最新更新的
          <span className={styles.privacyContentLink} onClick={onLink}>
            《麦当劳隐私政策》
          </span>
          。若您点击“同意”，即表示您已阅读并同意上述条款，麦当劳将严格按照
          <span className={styles.privacyContentLink} onClick={onLink}>
            《麦当劳隐私政策》
          </span>
          的各项条款使用和保护您的个人信息。若您点击“不同意”，可以选择退出。
          <br />
          进入麦当劳应用后，您可以在我的-条款与规则页面查看《隐私政策》
        </div>
      </div>
    </div>
  );
};

type NewPrivacyContentProps = {
  className?: string;
  style?: CSSProperties;
  title?: string;
  config?: CommonConfigType['privacyPopContext'];
  onLink?: (link: string) => void;
};

const NewPrivacyContent: FC<NewPrivacyContentProps> = (props) => {
  const { className, style, title, config, onLink } = props;

  const handleClick = useCallback(
    (url: string, e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      onLink?.(url);
    },
    [onLink],
  );

  const terms = useMemo(() => config?.terms?.filter((t) => t.termsCn && t.termsurlCn), [config?.terms]);

  return (
    <div className={classNames(styles.privacyContent, className)} style={style}>
      <div className={styles.privacyContentTitle}>{title || '麦当劳隐私政策提示'}</div>
      <div className={styles.privacyConfirmScroll}>
        <div className={styles.privacyContentText} style={{ whiteSpace: 'pre-wrap' }}>
          {config?.noticeCn}
        </div>
        {terms && (
          <div className={styles.privacyContentText} style={{ marginTop: '15px' }}>
            {terms.map((t) => (
              <div key={t.termsCn} className={styles.privacyContentLink2} onClick={(e) => handleClick(t.termsurlCn, e)}>
                {t.termsCn}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export type PrivacyConfirmProps = {
  className?: string;
  style?: CSSProperties;
  /** 确认操作样式。'1' | '2' 1授权弹窗 2提醒弹窗 @default 1 */
  actionStyle?: string;
  onPrivacyLink?: PrivacyContentProps['onLink'];
  onCustomPrivacyLink?: NewPrivacyContentProps['onLink'];
  onConfirm?: (e: MouseEvent<HTMLButtonElement>) => any;
  onCancel?: MouseEventHandler<HTMLDivElement>;
};

const PrivacyConfirm: FC<PrivacyConfirmProps> = (props) => {
  const { className, style, actionStyle = '1', onPrivacyLink, onCustomPrivacyLink, onConfirm, onCancel } = props;

  const hasLocalRecord = !!getLocalPrivacyVersionValue();

  const [res] = useResource(getCommonConfigRefConfig, [], { filter: () => hasLocalRecord });
  const config = useMemo(() => res.data?.config.privacyPopContext, [res.data]);

  const renderContent = () => {
    if (res.isLoading === true) {
      return (
        <div className={styles.privacyConfirmContent}>
          <Skeleton style={{ '--width': '150px', '--height': '18px', margin: '0 auto 25px' }} />
          <Skeleton.Paragraph lineCount={5} />
        </div>
      );
    }

    if (hasLocalRecord && config?.noticeCn) {
      return (
        <NewPrivacyContent
          className={styles.privacyConfirmContent}
          title="麦当劳条款更新提示"
          config={config}
          onLink={onCustomPrivacyLink}
        />
      );
    }
    return <PrivacyContent className={styles.privacyConfirmContent} onLink={onPrivacyLink} />;
  };

  return (
    <div className={className} style={style}>
      {renderContent()}
      <div className={styles.privacyConfirmActions}>
        {String(actionStyle) === '2' ? (
          <Button
            shape="rounded"
            color="primary"
            size="small"
            className={styles.privacyConfirmAgree}
            disabled={res.isLoading}
            onClick={onConfirm}>
            知道啦
          </Button>
        ) : (
          <>
            <Button
              shape="rounded"
              color="primary"
              size="small"
              className={styles.privacyConfirmAgree}
              disabled={res.isLoading}
              onClick={onConfirm}>
              同意
            </Button>
            <div className={styles.privacyConfirmExit} onClick={onCancel}>
              不同意并退出
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PrivacyConfirm;
